import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Header from "./components/Header";
import BackgroundImage from "./components/BackgroundImage";
import FloatingElements from "./components/FloatingElements";
import HomePage from "./components/HomePage";
import OffersPage from "./components/OffersPage";
import Dashboard from "./components/Dashboard";
import OffersPageNew from "./components/OffersPageNew";
import EventsPage from "./components/EventsPage";
import Navbar from "./components/Navbar";
import ProfileModal from "./components/ProfileModal";
import LoginModal from "./components/auth/login/LoginModal";
import ForgotPasswordModal from "./components/auth/forgot_password/ForgotPasswordModal";
import RegistrationForm from "./components/auth/registration/RegistrationForm";
import BackOfficeApp from "./components/backoffice/auth/BackOfficeApp";
import Footer from "./components/Footer";
import SplitLayout from "./components/SplitLayout";
import { supabase } from "./utils/supabase";
import ClubMiaAuthService from "./services/clubMiaAuth";
import { extractDisplayName, extractFirstName } from "./utils/nameUtils";

// Componente principal que maneja la navegación
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userProfile, setUserProfile] = useState({});
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] =
    useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [showBackOffice, setShowBackOffice] = useState(false);
  const [webConfig, setWebConfig] = useState({
    primary_color: "#9e187e", // Default
    secondary_color: "#7d1463", // Default
    site_title: "Mia Club", // Default
    logo: {
      url: "/mia_club_logo.webp",
      alt_text: "Supermercados Mia",
    }, // Default
  });

  // useEffect(() => {
  //   const fetchWebConfig = async () => {
  //     const { data, error } = await supabase.from("web_config").select("*");

  //     if (error) {
  //       console.error("Error fetching web config:", error);
  //     } else {
  //       const fetchedConfig = {};
  //       data.forEach((item) => {
  //         fetchedConfig[item.config_name] = item.config_value;
  //       });
  //       setWebConfig((prevConfig) => ({
  //         ...prevConfig,
  //         ...fetchedConfig,
  //         logo: fetchedConfig.logo || {
  //           url: "https://supermercadosmia.siansystem.com/supermercadosmia/images/logos/logo.png",
  //           alt_text: "Supermercados Mia",
  //         },
  //       }));
  //     }
  //   };

  //   fetchWebConfig();
  //   window.addEventListener("webConfigUpdated", fetchWebConfig);

  //   return () => {
  //     window.removeEventListener("webConfigUpdated", fetchWebConfig);
  //   };
  // }, []);

  // Verificar sesión guardada al cargar la aplicación
  useEffect(() => {
    const checkSavedSession = () => {
      const currentUser = ClubMiaAuthService.getCurrentUser();
      const currentPerson = ClubMiaAuthService.getCurrentPerson();

      if (currentUser && currentPerson) {
        const extractedFirstName = extractFirstName(currentPerson?.person_name);
        const displayName = extractDisplayName(currentPerson?.person_name);

        const combinedProfile = {
          ...currentUser,
          firstName: extractedFirstName || currentUser.firstName,
          displayName: displayName || extractedFirstName || currentUser.firstName,
          fullName: currentPerson?.person_name || currentUser.firstName,
          personId: currentPerson?.person_id,
          identificationNumber: currentPerson?.identification_number,
          agreementId: currentPerson?.agreement_id,
        };

        setUserProfile(combinedProfile);
        setIsLoggedIn(true);
      }
    };

    checkSavedSession();
  }, []);

  // Aplicar colores a variables CSS
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--primary-color",
      webConfig.primary_color
    );
    document.documentElement.style.setProperty(
      "--secondary-color",
      webConfig.secondary_color
    );
  }, [webConfig.primary_color, webConfig.secondary_color]);

  const handleAdminLogin = () => {
    setShowBackOffice(true);
  };

  const handleGoToHome = () => {
    setShowBackOffice(false);
    setIsLoginModalOpen(false);
    setIsForgotPasswordModalOpen(false);
    navigate('/');
  };

  const handleNavigateToRegister = () => {
    setIsLoginModalOpen(false);
    setShowBackOffice(false);
    navigate('/registro');
  };

  const handleNavigateToLogin = () => {
    setIsLoginModalOpen(true);
    setShowBackOffice(false);
  };

  const handleRegistrationSuccess = (responseData) => {
    console.log("Registration response data:", responseData);

    // Extract user and person data from the response
    const userData = responseData.user || {};
    const personData = responseData.person || {};

    // Create combined profile similar to login
    const extractedFirstName = extractFirstName(personData?.person_name);
    const displayName = extractDisplayName(personData?.person_name);

    const combinedProfile = {
      ...userData,
      // Use person data for the name
      firstName: extractedFirstName || userData.firstname,
      displayName: displayName || extractedFirstName || userData.firstname,
      fullName: personData?.person_name || userData.firstname,
      personId: personData?.person_id,
      identificationNumber: personData?.identification_number,
      agreementId: personData?.agreement_id,
      webUserId: userData?.web_user_id,
      email: userData?.email_address,
      phone: userData?.phone_number,
    };

    setUserProfile(combinedProfile);
    setIsLoggedIn(true);

    // Redirect to offers page like login does
    navigate('/ofertas');
  };



  const handleLoginSuccess = (userData) => {
    // Combinar datos de user y person para el perfil
    const personData = ClubMiaAuthService.getCurrentPerson();
    const extractedFirstName = extractFirstName(personData?.person_name);
    const displayName = extractDisplayName(personData?.person_name);

    const combinedProfile = {
      ...userData,
      // Usar datos de person para el nombre
      firstName: extractedFirstName || userData.firstName,
      displayName: displayName || extractedFirstName || userData.firstName, // Para mostrar en dashboard
      fullName: personData?.person_name || userData.firstName,
      personId: personData?.person_id,
      identificationNumber: personData?.identification_number,
      agreementId: personData?.agreement_id,
    };
    setUserProfile(combinedProfile);
    setIsLoggedIn(true);
    setIsLoginModalOpen(false);

    // Redirigir directamente a ofertas después del login
    navigate('/ofertas');
  };

  const handleSaveProfile = (updatedProfile) => {
    setUserProfile(updatedProfile);
    console.log("Perfil actualizado en App.js:", updatedProfile);
  };

  const handleLogout = () => {
    // Limpiar localStorage usando el servicio
    ClubMiaAuthService.logout();

    // Actualizar estado local
    setIsLoggedIn(false);
    setUserProfile({});
    setShowBackOffice(false);

    // Redirigir al home
    navigate('/');
  };

  const handleOpenForgotPassword = () => {
    setIsLoginModalOpen(false);
    setIsForgotPasswordModalOpen(true);
  };

  const handleEditProfile = () => {
    setIsProfileModalOpen(true);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
  };

  if (showBackOffice) {
    return <BackOfficeApp onGoToHome={handleGoToHome} />;
  }

  return (
      <div className="min-h-screen relative flex flex-col w-full max-w-full overflow-x-hidden" style={{ minHeight: '100vh' }}>
        <BackgroundImage />
        <FloatingElements />

        <Header
          onNavigateToLogin={handleNavigateToLogin}
          onNavigateToRegister={handleNavigateToRegister}
          isLoggedIn={isLoggedIn}
          userProfile={userProfile}
          onLogout={handleLogout}
          onGoToHome={handleGoToHome}
          webConfig={webConfig} // Pasa la configuración web al Header
        />

        <div className="z-20 flex-grow">
          {/* Navbar para usuarios logueados */}
          {isLoggedIn && (
            <Navbar
              onEditProfile={handleEditProfile}
              onLogout={handleLogout}
              userName={userProfile.displayName || userProfile.firstName}
            />
          )}

          <Routes>
            <Route
              path="/"
              element={
                (() => {
                  console.log('🔐 App.js - isLoggedIn:', isLoggedIn, 'userProfile:', userProfile);
                  return isLoggedIn ? (
                    <Dashboard userProfile={userProfile} />
                  ) : (
                    <SplitLayout showBanners={true}>
                      <div className="pt-24 px-4 lg:px-8 py-8">
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.8 }}
                          className="w-full"
                        >
                          <HomePage
                            onNavigateToRegister={handleNavigateToRegister}
                            onNavigateToLogin={handleNavigateToLogin}
                            onAdminLogin={handleAdminLogin}
                            webConfig={webConfig}
                          />
                        </motion.div>
                      </div>
                    </SplitLayout>
                  );
                })()
              }
            />

            {/* Rutas protegidas para usuarios logueados */}
            <Route
              path="/dashboard"
              element={
                isLoggedIn ? (
                  <Dashboard userProfile={userProfile} />
                ) : (
                  <SplitLayout showBanners={true}>
                    <div className="pt-24 px-4 lg:px-8 py-8">
                      <HomePage
                        onNavigateToRegister={handleNavigateToRegister}
                        onNavigateToLogin={handleNavigateToLogin}
                        onAdminLogin={handleAdminLogin}
                        webConfig={webConfig}
                      />
                    </div>
                  </SplitLayout>
                )
              }
            />

            <Route
              path="/ofertas"
              element={
                isLoggedIn ? (
                  <OffersPageNew userProfile={userProfile} />
                ) : (
                  <SplitLayout showBanners={true}>
                    <div className="pt-24 px-4 lg:px-8 py-8">
                      <HomePage
                        onNavigateToRegister={handleNavigateToRegister}
                        onNavigateToLogin={handleNavigateToLogin}
                        onAdminLogin={handleAdminLogin}
                        webConfig={webConfig}
                      />
                    </div>
                  </SplitLayout>
                )
              }
            />

            <Route
              path="/eventos"
              element={
                isLoggedIn ? (
                  <EventsPage userProfile={userProfile} />
                ) : (
                  <SplitLayout showBanners={true}>
                    <div className="pt-24 px-4 lg:px-8 py-8">
                      <HomePage
                        onNavigateToRegister={handleNavigateToRegister}
                        onNavigateToLogin={handleNavigateToLogin}
                        onAdminLogin={handleAdminLogin}
                        webConfig={webConfig}
                      />
                    </div>
                  </SplitLayout>
                )
              }
            />
            <Route
              path="/registro"
              element={
                <SplitLayout showBanners={true}>
                  <div className="pt-24 px-4 lg:px-8 py-8 flex items-center justify-center">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.8 }}
                      className="w-full max-w-lg lg:mt-8"
                    >
                      <RegistrationForm
                        onRegistrationSuccess={handleRegistrationSuccess}
                        onNavigateToLogin={handleNavigateToLogin}
                        onGoToHome={handleGoToHome}
                      />
                    </motion.div>
                  </div>
                </SplitLayout>
              }
            />
          </Routes>
        </div>

        <Footer onAdminLogin={handleAdminLogin} />

        <LoginModal
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={handleLoginSuccess}
          onForgotPassword={handleOpenForgotPassword}
        />

        <ForgotPasswordModal
          isOpen={isForgotPasswordModalOpen}
          onClose={() => setIsForgotPasswordModalOpen(false)}
        />

        <ProfileModal
          isOpen={isProfileModalOpen}
          onClose={handleCloseProfileModal}
          userProfile={userProfile}
          onSaveProfile={handleSaveProfile}
        />
      </div>
  );
};

// Componente App principal que envuelve todo en BrowserRouter
const App = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;
